/**
 * LegendControl - Interactive map legend with layer visibility, opacity,
 * drag-to-reorder, download links, and metadata links.
 *
 * Supports both static layer definitions and dynamic catalog layers
 * discovered from pycsw OGC API - Records.
 *
 * Addresses: REQ-LEGEND-001 through REQ-LEGEND-015
 */
import Map from 'ol/Map';
import View from 'ol/View';
import Feature from 'ol/Feature';
import TileLayer from 'ol/layer/Tile';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorLayer from 'ol/layer/Vector';
import WebGLTileLayer from 'ol/layer/WebGLTile';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import OGCVectorTile from 'ol/source/OGCVectorTile';
import { PMTilesVectorSource } from 'ol-pmtiles';
import GeoTIFF from 'ol/source/GeoTIFF';
import TileWMS from 'ol/source/TileWMS';
import WMTS, { optionsFromCapabilities as wmtsOptionsFromCapabilities } from 'ol/source/WMTS';
import XYZ from 'ol/source/XYZ';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import GeoJSON from 'ol/format/GeoJSON';
import MVT from 'ol/format/MVT';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import Polygon from 'ol/geom/Polygon';
import { transformExtent } from 'ol/proj';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import { Stroke, Fill, Style, Circle as CircleStyle } from 'ol/style';
import { loadSldStyle } from './sld-style';
import SecimsBboxThumbnail from '../../js/bbox-thumbnail.js';
import SecimsConditionsConfirm from '../../js/conditions-confirm.js';

// OL primitives the shared bbox-thumbnail helper needs. Passed via the
// `olDeps` option because the helper auto-resolves from window.ol in
// non-bundled contexts (data-catalog, landing) but receives them
// explicitly here since the webpack build never populates window.ol.
const OL_DEPS = {
  Map, View, Feature, Polygon,
  TileLayer, VectorLayer, VectorSource, OSM,
  Style, Stroke, Fill,
  transformExtent
};

// ── Metadata modal renderer ──────────────────────────────────────────────────

function _renderMetadata(data) {
  var props = data.properties || {};
  var links = data.links || [];
  var html = '<div class="metadata-sections">';

  // Title & description
  html += _section('Overview',
    _row('Title', props.title) +
    _row('Type', _badge(props.type)) +
    _row('Description', props.description) +
    _dateRows(props)
  );

  // Contacts
  var contacts = props.contacts || [];
  if (contacts.length) {
    var contactHtml = '';
    contacts.forEach(function (c) {
      contactHtml += '<div class="metadata-contact">' +
        '<div class="metadata-contact-type">' + _esc(_contactTypeLabel(c)) + '</div>' +
        (c.name ? '<strong>' + _esc(c.name) + '</strong>' : '') +
        (c.organization ? '<br>' + _esc(c.organization) : '') +
        (c.position ? '<br><em>' + _esc(c.position) + '</em>' : '') +
        _contactEmails(c) +
        '</div>';
    });
    html += _section('Contacts', contactHtml);
  }

  // Spatial
  var extentHtml = '';
  // Resolve bbox from top-level field or fall back to geometry.
  var bboxNums = SecimsBboxThumbnail.resolveBbox(data);
  if (bboxNums) {
    extentHtml += _row('Bounding Box',
      'W ' + bboxNums[0] + ', S ' + bboxNums[1] + ', E ' + bboxNums[2] + ', N ' + bboxNums[3]);
    // Mounted by SecimsBboxThumbnail.initBboxMap after innerHTML is set on the modal body.
    extentHtml += '<div class="metadata-bbox-map"></div>';
  }
  if (props.crs) extentHtml += _row('CRS', _formatCrs(props.crs));
  if (props.spatialRepresentationType)
    extentHtml += _row('Spatial Representation', props.spatialRepresentationType);
  if (props.spatialResolution)
    extentHtml += _row('Scale', '1:' + props.spatialResolution);
  if (extentHtml) html += _section('Spatial', extentHtml);

  // Themes / Topics
  var themes = props.themes || [];
  if (themes.length) {
    var topicHtml = '';
    themes.forEach(function (t) {
      var concepts = (t.concepts || [])
        .map(function (c) { return c.id || c; })
        .filter(function (c) {
          return typeof c === 'string' && c && c.indexOf('urn:x-secims:') !== 0;
        });
      topicHtml += concepts.map(function (c) {
        return '<span class="metadata-badge">' + _esc(c) + '</span>';
      }).join(' ');
    });
    if (topicHtml) html += _section('Topics', topicHtml);
  }

  // Constraints
  var constraintsHtml = '';
  if (props.rights) constraintsHtml += _row('Use Conditions', props.rights);
  if (props.otherConstraints) constraintsHtml += _row('Access Conditions', props.otherConstraints);
  if (props.accessConstraints && !props.otherConstraints) {
    constraintsHtml += _row('Access Conditions', props.accessConstraints);
  }
  if (constraintsHtml) html += _section('Constraints', constraintsHtml);

  // Lineage
  var lineage = props.lineage || {};
  if (lineage.statement) {
    var lineageHtml = _row('Statement', lineage.statement);
    if (lineage.source && lineage.source.length) {
      lineageHtml += _row('Sources', lineage.source.map(function (s) {
        return _esc(s.description || s.citation_title || JSON.stringify(s));
      }).join('<br>'));
    }
    html += _section('Lineage', lineageHtml);
  }

  // Distribution / Links
  if (props.formats || links.length) {
    var distHtml = '';
    if (props.formats && props.formats.length) {
      // Drop SEC-IMS-internal urn identifiers (e.g. urn:x-secims:file:id)
      // — these are opaque references to the dataset file, not user-facing
      // format names. Standard urns (e.g. urn:ogc:...) are kept.
      var fmtNames = props.formats.map(function (f) {
        return typeof f === 'object' ? f.name : f;
      }).filter(function (n) { return n && n.indexOf('urn:x-secims:') !== 0; });
      if (fmtNames.length) distHtml += _row('Formats', fmtNames.join(', '));
    }
    var dlLinks = links.filter(function (l) {
      return l.protocol && !l.protocol.startsWith('OGC:') && !l.protocol.startsWith('urn:');
    });
    if (dlLinks.length) {
      distHtml += '<div class="metadata-links">';
      dlLinks.forEach(function (l) {
        distHtml += '<a href="' + _esc(l.href) + '" target="_blank" rel="noopener">' +
          _esc(l.name || l.protocol) + '</a> ';
      });
      distHtml += '</div>';
    }
    if (distHtml) html += _section('Distribution', distHtml);
  }

  html += '</div>';
  return html;
}

function _section(title, content) {
  return '<div class="metadata-section">' +
    '<h4>' + _esc(title) + '</h4>' +
    '<div class="metadata-section-body">' + content + '</div>' +
    '</div>';
}

// Strip the OGC-Naming-Authority urn boilerplate from a CRS identifier
// so users see "EPSG:4326" instead of "urn:ogc:def:crs:EPSG::4326".
// Standard urn shape is urn:ogc:def:crs:<authority>:<version>:<code>;
// version is usually empty for current revisions. Returns the input
// unchanged if it doesn't match (e.g. already "EPSG:4326", or an
// http:// URL form).
function _formatCrs(crs) {
  if (typeof crs !== 'string') return crs;
  var m = /^urn:ogc:def:crs:([^:]+):[^:]*:([^:]+)$/i.exec(crs);
  if (m) return m[1] + ':' + m[2];
  return crs;
}

function _row(label, value) {
  if (!value && value !== 0) return '';
  // Drop rows whose value is an internal SEC-IMS urn (e.g.
  // urn:x-secims:file:<uuid> file references that leak through
  // pycsw). Standard ISO urns like urn:ogc:def:crs:EPSG::4326 must
  // still render — they're legitimate identifiers, not internal noise.
  if (typeof value === 'string' && value.indexOf('urn:x-secims:') === 0) return '';
  return '<div class="metadata-row">' +
    '<span class="metadata-label">' + _esc(label) + '</span>' +
    '<span class="metadata-value">' + _esc(String(value)) + '</span>' +
    '</div>';
}

function _badge(val) {
  return val || '';
}

function _date(val) {
  if (!val) return '';
  return String(val).substring(0, 10);
}

// Render the three CI_Date rows (Creation / Publication / Revision) in
// fixed order, pulled from the three SLUISO-exposed properties. `updated`
// is intentionally NOT used here — that field carries the metadata's
// system timestamp, not the user-entered dataset Revision date. _row
// returns empty string for falsy values so absent dates drop out.
function _dateRows(props) {
  return _row('Creation',    _date(props.created))  +
         _row('Publication', _date(props.issued))   +
         _row('Revision',    _date(props.revision));
}

function _contactEmails(contact) {
  var emails = [];
  if (contact.email) emails.push(contact.email);
  if (contact.emails) contact.emails.forEach(function (e) {
    emails.push(e.value || e);
  });
  if (!emails.length) return '';
  return '<br>' + emails.map(function (e) {
    return '<a href="mailto:' + _esc(e) + '">' + _esc(e) + '</a>';
  }).join(', ');
}

// Map a contact's contacttype/roles to a human-readable header.
// Reads the shared CODELISTS global loaded from
// /metadata-editor/js/codelists.js (script tag in map/index.html), so
// the editor owns the canonical CI_RoleCode → label mapping. Two
// overrides: 'metadata' is a pycsw-internal contacttype (no ISO role
// equivalent), and 'pointOfContact' is renamed to 'Dataset Contact' to
// make the metadata-vs-dataset distinction obvious in the modal.
var CONTACT_TYPE_OVERRIDES = {
  metadata: 'Metadata Contact',
  pointOfContact: 'Dataset Contact'
};

function _contactTypeLabel(contact) {
  var key = contact.contacttype ||
            (Array.isArray(contact.roles) && contact.roles[0]) ||
            '';
  if (CONTACT_TYPE_OVERRIDES[key]) return CONTACT_TYPE_OVERRIDES[key];
  var codelists = typeof window !== 'undefined' ? window.CODELISTS : null;
  if (codelists && Array.isArray(codelists.role)) {
    var hit = codelists.role.find(function (r) { return r.value === key; });
    if (hit) return hit.label;
  }
  if (!key) return 'Contact';
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
}

function _esc(str) {
  var d = document.createElement('div');
  d.textContent = str;
  return d.innerHTML;
}

// Bbox preview map + PNG capture live in the shared helper at
// secims_view/js/bbox-thumbnail.js (imported as SecimsBboxThumbnail above).

// ── Print-to-PDF helper ──────────────────────────────────────────────────────

function _printMetadataAsPdf(title, bodyHtml) {
  var win = window.open('', '_blank');
  win.document.write(
    '<!DOCTYPE html><html><head>' +
    '<title>' + title + '</title>' +
    '<style>' +
      'body { font-family: "Segoe UI", Arial, sans-serif; font-size: 13px; color: #333; margin: 30px; }' +
      'h2 { color: #003f7a; margin: 0 0 20px 0; font-size: 20px; border-bottom: 3px solid #0057a8; padding-bottom: 8px; }' +
      '.metadata-section { margin-bottom: 14px; }' +
      '.metadata-section h4 { margin: 0 0 6px 0; padding: 4px 0; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #003f7a; border-bottom: 2px solid #0057a8; }' +
      '.metadata-section-body { padding-left: 2px; }' +
      '.metadata-row { display: flex; padding: 3px 0; border-bottom: 1px solid #eee; }' +
      '.metadata-label { flex: 0 0 140px; font-weight: 600; color: #555; font-size: 12px; }' +
      '.metadata-value { flex: 1; word-break: break-word; }' +
      '.metadata-badge { display: inline-block; background: #e8f0f9; color: #003f7a; padding: 1px 6px; border-radius: 3px; font-size: 11px; margin: 1px; }' +
      '.metadata-contact { padding: 6px 0; border-bottom: 1px solid #eee; line-height: 1.5; }' +
      '.metadata-contact:last-child { border-bottom: none; }' +
      '.metadata-contact-type { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #003f7a; margin-bottom: 3px; }' +
      /* The empty placeholder div is hidden — the snapshot, if captured,
         is swapped in as an <img class="metadata-bbox-map-image">. */
      '.metadata-bbox-map { display: none; }' +
      '.metadata-bbox-map-image { display: block; max-width: 100%; height: auto; margin-top: 8px; border: 1px solid #ddd; border-radius: 4px; }' +
      '.metadata-links a { display: inline-block; background: #0057a8; color: #fff; padding: 3px 8px; border-radius: 3px; font-size: 11px; text-decoration: none; margin: 2px; }' +
      '.metadata-modal-error { color: #c62828; }' +
      '@media print { body { margin: 15px; } }' +
    '</style>' +
    '</head><body>' +
    '<h2>' + title + '</h2>' +
    bodyHtml +
    '</body></html>'
  );
  win.document.close();
  win.focus();
  win.print();
}

// ── LegendControl class ──────────────────────────────────────────────────────

export default class LegendControl {
  /**
   * @param {import('ol/Map').default} map - The OpenLayers map instance
   * @param {Object} config
   * @param {Array} config.projectLayers - Array of layer definitions for project layers
   * @param {Array} config.basemapLayers - Array of layer definitions for basemap layers
   * @param {Array} [catalogLayers=[]] - Dataset layer descriptors from catalog-client.js
   * @param {import('./MapKey').default|null} [mapKey=null] - Cartographic legend
   * @param {Array} [catalogServices=[]] - Service descriptors (one per link) from
   *   catalog-client.js#fetchCatalogServices(). Grouped by record id inside.
   *
   * Each layer definition:
   * {
   *   label: string,          // display name
   *   layer: ol.layer.Base,   // OL layer reference
   *   visible: boolean,       // initial visibility
   *   downloadUrl: string|null,
   *   metadataUrl: string|null
   * }
   */
  constructor(map, config, catalogLayers = [], mapKey = null, catalogServices = []) {
    this.map = map;
    // Cartographic legend (above the scale bar). Optional — if null,
    // visibility/style notifications are no-ops.
    this.mapKey = mapKey;

    // Create OL layers from catalog descriptors and merge with static project layers
    const catalogLayerDefs = this._buildCatalogLayerDefs(catalogLayers);
    this.projectLayers = [...catalogLayerDefs, ...(config.projectLayers || [])];
    this.basemapLayers = config.basemapLayers || [];

    // External-service descriptors (WMS/WMTS/XYZ/ArcGIS REST). May be
    // empty — the section still renders with a placeholder message so
    // operators can see the feature exists even before any service
    // records are catalogued.
    this.webServices = [
      ...this._buildCatalogServiceDefs(catalogServices),
      ...(config.webServices || [])
    ];

    // Track drag state
    this._dragSourceItem = null;
    this._dragSourceSection = null;

    this._buildPanel();
    this._bindCollapseToggle();
    this._syncMapLayerOrder();
  }

  // ── Catalog layer factories ──────────────────────────────────────────

  /**
   * Convert catalog layer descriptors into legend layer definitions.
   * Groups by record ID so one record with Features+Tiles becomes one legend entry
   * using the best available source (prefer tiles for performance).
   */
  _buildCatalogLayerDefs(catalogLayers) {
    // Group by record ID
    const byRecord = {};
    for (const cl of catalogLayers) {
      if (!byRecord[cl.id]) {
        byRecord[cl.id] = { title: cl.title, description: cl.description,
                            bbox: cl.bbox, metadataUrl: cl.metadataUrl, links: [] };
      }
      byRecord[cl.id].links.push(cl);
    }

    const defs = [];
    for (const [id, rec] of Object.entries(byRecord)) {
      // Prefer vector tiles, fall back to features, then coverages.
      const tileLink = rec.links.find(l => l.protocol === 'OGC:API - Tiles');
      const featureLink = rec.links.find(l => l.protocol === 'OGC:API - Features');
      const coverageLink = rec.links.find(l => l.protocol === 'OGC:API - Coverages');

      let layer;
      let chosenProtocol;
      if (tileLink) {
        layer = this._createTileLayer(tileLink);
        chosenProtocol = 'OGC:API - Tiles';
      } else if (featureLink) {
        layer = this._createFeatureLayer(featureLink);
        chosenProtocol = 'OGC:API - Features';
      } else if (coverageLink) {
        layer = this._createCoverageLayer(coverageLink);
        chosenProtocol = 'OGC:API - Coverages';
      } else {
        continue; // skip unsupported protocols for now
      }

      if (!layer) continue;

      // Add to map
      this.map.addLayer(layer);

      // Derive the legend icon from how the layer is actually rendered
      // rather than the record's spatialRepresentationType property,
      // which can be stale or wrong (e.g. raster uploads where the user
      // didn't change the form's default).
      const isRaster = chosenProtocol === 'OGC:API - Coverages';
      const spatialRepType = isRaster
        ? 'grid'
        : (rec.links[0].spatialRepType || 'vector');

      // Use whichever link first surfaced the styleUrl (it's record-
      // scoped so all rec.links share the same value).
      const styleUrl = rec.links[0].styleUrl || null;

      defs.push({
        label: rec.title,
        layer: layer,
        visible: false,
        downloadUrl: featureLink ? featureLink.serviceUrl : null,
        // Coverages endpoint for the bbox-clipped raster download; null
        // for vector layers. Carried through so _buildAccordion can
        // optionally render a "Download GeoTIFF" link next to Metadata.
        coverageUrl: coverageLink ? coverageLink.serviceUrl : null,
        // Record bbox in CRS84 ([west, south, east, north]) — consumed by
        // the "Zoom to Extent" accordion link.
        bbox: rec.bbox || null,
        metadataUrl: rec.metadataUrl,
        spatialRepType: spatialRepType,
        // OGC API - Styles stylesheet URL, applied lazily on first
        // visibility toggle. Skipped for raster layers (chosenProtocol
        // === 'OGC:API - Coverages') since they use WebGL color
        // expressions rather than OL Style functions.
        styleUrl: !isRaster ? styleUrl : null,
        // Async-load bookkeeping populated during the first toggle.
        _stylePromise: null,
        _styleLoaded: false
      });
    }

    return defs;
  }

  /**
   * Create a vector layer from OGC API - Features endpoint.
   * Uses bbox loading strategy for large datasets.
   */
  _createFeatureLayer(layerDesc) {
    const source = new VectorSource({
      format: new GeoJSON(),
      url: function (extent) {
        // bboxStrategy hands us the view's extent in its own projection
        // (EPSG:3857 in this viewer). OGC API - Features defaults bbox
        // to CRS84 (lon/lat); sending mercator metres with a fake
        // EPSG:4326 suffix produced a 400. Transform first.
        const extent4326 = transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
        const bbox = extent4326.map(function (n) { return n.toFixed(6); }).join(',');
        const sep = layerDesc.serviceUrl.includes('?') ? '&' : '?';
        return `${layerDesc.serviceUrl}${sep}f=json&limit=1000&bbox=${bbox}`;
      },
      strategy: bboxStrategy
    });

    return new VectorLayer({
      source,
      visible: false,
      style: this._defaultVectorStyle(),
      properties: {
        catalogId: layerDesc.id,
        title: layerDesc.title,
        attributionOrg: layerDesc.attributionOrg || null
      }
    });
  }

  /**
   * Create a vector tile layer from an OGC API - Tiles endpoint.
   *
   * Starts with an OGCVectorTile source against the per-tile endpoint
   * (live MVT from MVT-postgresql, always current) and then
   * asynchronously probes the tileset metadata for a PMTiles archive
   * link. When the archive is advertised by the server (see
   * PMTilesProvider.get_default_metadata in secims_server), the
   * source is swapped to PMTilesVectorSource so subsequent tile loads
   * stream byte ranges directly from the .pmtiles file on disk via
   * the Flask passthrough route.
   *
   * The initial OGC source guarantees the layer renders even when
   * PMTiles discovery is slow or unavailable; the swap takes effect
   * silently once the metadata is read. Layers are created with
   * visible:false, so neither source actually loads tiles until the
   * user toggles the layer on.
   */
  _createTileLayer(layerDesc) {
    // OGCVectorTile expects the specific tileset URL, not the tiles listing
    let tilesetUrl = layerDesc.serviceUrl;
    if (tilesetUrl.endsWith('/tiles')) {
      tilesetUrl += '/WebMercatorQuad';
    }

    const source = new OGCVectorTile({
      url: tilesetUrl,
      format: new MVT(),
      mediaType: 'application/vnd.mapbox-vector-tile'
    });

    const layer = new VectorTileLayer({
      source,
      visible: false,
      style: this._defaultVectorStyle(),
      properties: {
        catalogId: layerDesc.id,
        title: layerDesc.title,
        attributionOrg: layerDesc.attributionOrg || null
      }
    });

    this._upgradeToPMTilesIfAvailable(layer, tilesetUrl);
    return layer;
  }

  /**
   * Probe the tile passthrough URL directly to discover whether the
   * collection ships a PMTiles archive sibling, and swap the layer's
   * source to a PMTilesVectorSource when it does.
   *
   * Why probe the passthrough URL instead of the OGC tile-set JSON:
   * pygeoapi's tile-metadata endpoint surfaces only the **first**
   * tile provider's links. Our collection config orders MVT-postgresql
   * first (so the per-tile OGC endpoint serves live PostGIS data),
   * which means the JSON never advertises the PMTiles sibling even
   * when one exists. The passthrough route, however, is iterative
   * over all tile providers (see secims_server's Flask short-circuit
   * in get_collection_tiles_metadata) and will serve the archive on
   * ?f=pmtiles whenever a PMTiles provider is configured.
   *
   * Probe: a 1-byte Range GET. 206 + Content-Type
   * application/vnd.pmtiles confirms the archive is reachable;
   * anything else leaves the OGCVectorTile source in place.
   */
  async _upgradeToPMTilesIfAvailable(layer, tilesetUrl) {
    const archiveUrl = `${tilesetUrl}?f=pmtiles`;
    try {
      const resp = await fetch(archiveUrl, {
        headers: { Range: 'bytes=0-0' }
      });
      if (resp.status !== 206 && resp.status !== 200) return;
      const ctype = (resp.headers.get('Content-Type') || '').toLowerCase();
      if (!ctype.includes('application/vnd.pmtiles')) return;

      const pmtilesSource = new PMTilesVectorSource({ url: archiveUrl });
      layer.setSource(pmtilesSource);
    } catch (err) {
      // Fall back silently to the OGCVectorTile source already set.
    }
  }

  /**
   * Create a WebGL raster layer from an OGC API - Coverages endpoint.
   *
   * Appends `?f=GTiff` so pygeoapi serves the underlying COG. The
   * GeoTIFF source uses HTTP Range requests to read just the COG
   * headers + the IFD/tile byte ranges it needs for the current
   * viewport — relies on the server honoring Range on the coverage
   * passthrough (see SECIMS_CHANGES.md item 4 in secims_server).
   */
  _createCoverageLayer(layerDesc) {
    const sep = layerDesc.serviceUrl.includes('?') ? '&' : '?';
    const url = `${layerDesc.serviceUrl}${sep}f=GTiff`;

    const source = new GeoTIFF({
      sources: [{ url }],
      convertToRGB: 'auto'
    });

    return new WebGLTileLayer({
      source,
      visible: false,
      properties: {
        catalogId: layerDesc.id,
        title: layerDesc.title,
        attributionOrg: layerDesc.attributionOrg || null
      }
    });
  }

  // ── External web-service layer factories ────────────────────────────
  // Mirror the dataset-side `_buildCatalogLayerDefs` shape so the legend
  // section renderer can treat web services uniformly. Each factory
  // returns a created OL layer (already added to the map) or null when
  // the descriptor is unusable (e.g. WMS without a layer name).

  /**
   * Group service descriptors by record id and produce one legend def
   * per record. When a record advertises multiple protocols, pick the
   * most efficient renderable one (WMTS → WMS → XYZ → ESRI Rest).
   */
  _buildCatalogServiceDefs(serviceDescs) {
    const byRecord = {};
    for (const sd of serviceDescs) {
      if (!byRecord[sd.id]) {
        byRecord[sd.id] = {
          id: sd.id,
          title: sd.title,
          description: sd.description,
          bbox: sd.bbox,
          metadataUrl: sd.metadataUrl,
          links: []
        };
      }
      byRecord[sd.id].links.push(sd);
    }

    const PROTOCOL_PRIORITY = ['OGC:WMTS', 'OGC:WMS', 'XYZ Tiles', 'ESRI Rest Services'];
    const defs = [];

    for (const rec of Object.values(byRecord)) {
      let chosen = null;
      for (const protocol of PROTOCOL_PRIORITY) {
        const match = rec.links.find(l => l.protocol === protocol);
        if (match) { chosen = match; break; }
      }
      if (!chosen) continue;

      let layer = null;
      if (chosen.protocol === 'OGC:WMS') {
        layer = this._createWMSLayer(chosen);
      } else if (chosen.protocol === 'OGC:WMTS') {
        layer = this._createWMTSLayer(chosen);
      } else if (chosen.protocol === 'XYZ Tiles') {
        layer = this._createXYZLayer(chosen);
      } else if (chosen.protocol === 'ESRI Rest Services') {
        layer = this._createArcGISRestLayer(chosen);
      }
      if (!layer) continue;

      this.map.addLayer(layer);

      defs.push({
        label: rec.title,
        layer: layer,
        visible: false,
        downloadUrl: null,
        coverageUrl: null,
        bbox: rec.bbox || null,
        metadataUrl: rec.metadataUrl,
        // Drives the legend thumbnail: 'grid' renders the raster icon,
        // which suits tiled imagery layers from external services.
        spatialRepType: 'grid',
        // Web-service layers don't pass through SLD loading (their
        // server controls styling). Mark style as loaded so the
        // accordion's lazy-load path becomes a no-op.
        styleUrl: null,
        _styleLoaded: true,
        _stylePromise: null,
        // Surfaced in the accordion for transparency about the source.
        serviceProtocol: chosen.protocol,
        serviceUrl: chosen.serviceUrl
      });
    }

    return defs;
  }

  /**
   * WMS: requires a `layerName` (the LAYERS query param). The metadata
   * editor stores this in link.name (see state.js#webServices). Without
   * it the GetMap call returns an exception image, so skip rather than
   * surface a broken layer.
   */
  _createWMSLayer(desc) {
    if (!desc.layerName) {
      console.warn(`WMS service "${desc.title}" has no layer name — skipping`);
      return null;
    }
    const source = new TileWMS({
      url: desc.serviceUrl,
      params: {
        LAYERS: desc.layerName,
        TILED: true
      },
      crossOrigin: 'anonymous'
    });
    return new TileLayer({
      source,
      visible: false,
      properties: {
        catalogId: desc.id,
        title: desc.title,
        attributionOrg: desc.attributionOrg || null
      }
    });
  }

  /**
   * WMTS: needs the layer's tile matrix set + origin from the server's
   * GetCapabilities. We fetch lazily — the TileLayer is returned
   * immediately with no source, and once capabilities arrive we attach
   * a configured WMTS source. If the fetch or parse fails the layer
   * stays empty; an empty toggle is preferable to crashing legend
   * construction for one bad endpoint.
   */
  _createWMTSLayer(desc) {
    if (!desc.layerName) {
      console.warn(`WMTS service "${desc.title}" has no layer name — skipping`);
      return null;
    }
    const layer = new TileLayer({
      visible: false,
      properties: {
        catalogId: desc.id,
        title: desc.title,
        attributionOrg: desc.attributionOrg || null
      }
    });
    this._attachWMTSSource(layer, desc).catch(err =>
      console.warn(`WMTS source attach failed for "${desc.title}"`, err)
    );
    return layer;
  }

  async _attachWMTSSource(layer, desc) {
    const capsUrl = this._wmtsCapabilitiesUrl(desc.serviceUrl);
    const response = await fetch(capsUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status} fetching ${capsUrl}`);
    const text = await response.text();
    const caps = new WMTSCapabilities().read(text);
    // Prefer Web Mercator so the layer aligns with the OSM basemap; if
    // the server only advertises EPSG:4326 (or a custom GeoServer alias
    // like EPSG:900913), fall through to whichever matrix set wins.
    let options = wmtsOptionsFromCapabilities(caps, {
      layer: desc.layerName,
      matrixSet: 'EPSG:900913'
    });
    if (!options) {
      options = wmtsOptionsFromCapabilities(caps, {
        layer: desc.layerName,
        matrixSet: 'EPSG:3857'
      });
    }
    if (!options) {
      throw new Error(`No supported WMTS matrix set for layer "${desc.layerName}"`);
    }
    options.crossOrigin = 'anonymous';
    layer.setSource(new WMTS(options));
  }

  /**
   * Strip query string and the trailing /rest path (the GeoServer GWC
   * exposes both /wmts (KVP) and /wmts/rest (RESTful) URLs; only the
   * KVP endpoint serves GetCapabilities via the standard query.
   */
  _wmtsCapabilitiesUrl(serviceUrl) {
    let base = serviceUrl.split('?')[0];
    base = base.replace(/\/rest\/?$/, '');
    return `${base}?service=WMTS&request=GetCapabilities&version=1.0.0`;
  }

  _createXYZLayer(desc) {
    const source = new XYZ({
      url: desc.serviceUrl,
      crossOrigin: 'anonymous'
    });
    return new TileLayer({
      source,
      visible: false,
      properties: {
        catalogId: desc.id,
        title: desc.title,
        attributionOrg: desc.attributionOrg || null
      }
    });
  }

  _createArcGISRestLayer(desc) {
    const source = new TileArcGISRest({
      url: desc.serviceUrl,
      crossOrigin: 'anonymous'
    });
    return new TileLayer({
      source,
      visible: false,
      properties: {
        catalogId: desc.id,
        title: desc.title,
        attributionOrg: desc.attributionOrg || null
      }
    });
  }

  /**
   * Issue a GetFeatureInfo request to every visible WMS web-service
   * layer at the given click coordinate, in parallel. Returns an array
   * of `{ layerName, properties }` — one entry per WMS layer that
   * returned at least one feature. Caller is responsible for rendering.
   *
   * Limitations: WMTS, XYZ, and ArcGIS REST layers are skipped. WMTS
   * GetFeatureInfo isn't exposed by OL's source helper, and the other
   * two have no standard identify mechanism. Adding WMTS would require
   * a server-specific WMS fallback (e.g. GeoServer routes the same
   * layers via /ows) which we don't generalise here.
   *
   * @param {ol.Coordinate} coordinate - click coordinate in map projection
   * @param {number} resolution - current view resolution
   * @param {ol.proj.Projection} projection - current view projection
   * @returns {Promise<Array<{layerName: string, properties: Object}>>}
   */
  async getWebServiceInfoAt(coordinate, resolution, projection) {
    const requests = [];
    for (const def of this.webServices) {
      if (!def.layer.getVisible()) continue;
      if (def.serviceProtocol !== 'OGC:WMS') continue;
      const source = def.layer.getSource();
      if (!source || typeof source.getFeatureInfoUrl !== 'function') continue;

      const url = source.getFeatureInfoUrl(
        coordinate, resolution, projection,
        { INFO_FORMAT: 'application/json', FEATURE_COUNT: 1 }
      );
      if (!url) continue;

      requests.push(
        fetch(url)
          .then(r => (r.ok ? r.json() : null))
          .then(data => {
            const feature = data && data.features && data.features[0];
            if (!feature || !feature.properties) return null;
            return { layerName: def.label, properties: feature.properties };
          })
          .catch(err => {
            console.warn(`GetFeatureInfo failed for "${def.label}":`, err);
            return null;
          })
      );
    }

    const results = await Promise.all(requests);
    return results.filter(Boolean);
  }

  /**
   * Lazily fetch the OGC API - Styles stylesheet for `layerDef` and
   * apply it to the underlying OL layer. No-op if the descriptor
   * carries no styleUrl, or if the load has already happened (or is
   * already in flight). Failures fall back silently to the default
   * style; the layer still renders.
   */
  _loadStyleOnce(layerDef) {
    if (!layerDef.styleUrl) return Promise.resolve();
    if (layerDef._styleLoaded) return Promise.resolve();
    if (layerDef._stylePromise) return layerDef._stylePromise;

    var self = this;
    layerDef._stylePromise = loadSldStyle(layerDef.styleUrl)
      .then(function (result) {
        layerDef.layer.setStyle(result.styleFn);
        layerDef.layer.setOpacity(result.hoistedOpacity);
        // Slider/display may not exist yet if the user enabled the
        // layer before opening the accordion (the slider is created
        // inside _buildAccordion). They get picked up when the
        // accordion is built — re-applied here for the open case.
        var pct = Math.round(result.hoistedOpacity * 100);
        if (layerDef._opacitySlider) layerDef._opacitySlider.value = String(pct);
        if (layerDef._opacityDisplay) layerDef._opacityDisplay.textContent = pct + '%';
        // Cache rule summaries on the layerDef and push to the map key.
        // setLayer also registers it for visibility tracking so a later
        // checkbox toggle re-renders correctly.
        layerDef._ruleSummaries = result.ruleSummaries || [];
        layerDef._classificationAttributes = result.classificationAttributes || [];
        if (self.mapKey) self.mapKey.setLayer(
          layerDef,
          layerDef._ruleSummaries,
          layerDef._classificationAttributes
        );
        layerDef._styleLoaded = true;
      })
      .catch(function (err) {
        console.warn('SLD load failed for "' + layerDef.label + '" (' +
                     layerDef.styleUrl + ')', err);
        // Mark loaded anyway to prevent repeat attempts on every toggle.
        layerDef._styleLoaded = true;
      });
    return layerDef._stylePromise;
  }

  /**
   * Default style for dynamically discovered vector layers.
   */
  _defaultVectorStyle() {
    return new Style({
      stroke: new Stroke({ color: '#0057a8', width: 2 }),
      fill: new Fill({ color: 'rgba(0, 87, 168, 0.15)' }),
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: '#0057a8' }),
        stroke: new Stroke({ color: '#fff', width: 1 })
      })
    });
  }

  // ── Panel construction ─────────────────────────────────────────────

  _buildPanel() {
    this.panelEl = document.createElement('div');
    this.panelEl.id = 'legend-panel';

    // Toggle button
    this.toggleEl = document.createElement('button');
    this.toggleEl.id = 'legend-toggle';
    this.toggleEl.innerHTML =
      '<svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">' +
        '<polygon points="2,1 8,5 2,9" fill="#fff"/>' +
      '</svg>' +
      '<span>Layer List</span>';
    this.panelEl.appendChild(this.toggleEl);

    // Body (collapsible)
    this.bodyEl = document.createElement('div');
    this.bodyEl.id = 'legend-body';

    // Project layers section
    this.projectSectionEl = this._buildSection(
      'Project Layers', this.projectLayers, 'project'
    );
    this.bodyEl.appendChild(this.projectSectionEl);

    // Divider
    var divider1 = document.createElement('div');
    divider1.className = 'legend-divider';
    this.bodyEl.appendChild(divider1);

    // Web services section (external WMS / WMTS / XYZ / ArcGIS REST)
    this.webServiceSectionEl = this._buildSection(
      'Web Services', this.webServices, 'webservice'
    );
    this.bodyEl.appendChild(this.webServiceSectionEl);

    // Divider
    var divider2 = document.createElement('div');
    divider2.className = 'legend-divider';
    this.bodyEl.appendChild(divider2);

    // Basemap layers section
    this.basemapSectionEl = this._buildSection(
      'Basemap Layers', this.basemapLayers, 'basemap'
    );
    this.bodyEl.appendChild(this.basemapSectionEl);

    this.panelEl.appendChild(this.bodyEl);

    // Append to map container
    document.getElementById('map').appendChild(this.panelEl);
  }

  _buildSection(title, layerDefs, sectionKey) {
    var section = document.createElement('div');
    section.className = 'legend-section';
    section.setAttribute('data-section', sectionKey);

    // Section title
    var titleEl = document.createElement('div');
    titleEl.className = 'legend-section-title';
    titleEl.textContent = title;
    section.appendChild(titleEl);

    // Items container (drag target)
    var itemsContainer = document.createElement('div');
    itemsContainer.className = 'legend-items';

    if (layerDefs.length === 0) {
      var emptyEl = document.createElement('div');
      emptyEl.className = 'legend-empty';
      var emptyText = 'No layers loaded';
      if (sectionKey === 'project') emptyText = 'No project layers loaded';
      else if (sectionKey === 'webservice') emptyText = 'No web services configured';
      else if (sectionKey === 'basemap') emptyText = 'No basemap layers configured';
      emptyEl.textContent = emptyText;
      itemsContainer.appendChild(emptyEl);
    } else {
      for (var i = 0; i < layerDefs.length; i++) {
        var item = this._buildLayerItem(layerDefs[i], sectionKey);
        itemsContainer.appendChild(item);
      }
    }

    section.appendChild(itemsContainer);

    // Bind drag-and-drop events on the items container
    this._bindDragEvents(itemsContainer, sectionKey);

    return section;
  }

  _buildLayerItem(layerDef, sectionKey) {
    var item = document.createElement('div');
    item.className = 'legend-item';
    item.draggable = false;
    item._layerDef = layerDef;
    item._sectionKey = sectionKey;

    // ── Main row ───────────────────────────────────────────────────
    var row = document.createElement('div');
    row.className = 'legend-item-row';

    // Drag handle — only this element initiates drag
    var handle = document.createElement('span');
    handle.className = 'drag-handle';
    handle.innerHTML = '<svg viewBox="0 0 8 8" width="10" height="10"><circle cx="2" cy="2" r="1.2" fill="#666"/><circle cx="6" cy="2" r="1.2" fill="#666"/><circle cx="2" cy="6" r="1.2" fill="#666"/><circle cx="6" cy="6" r="1.2" fill="#666"/></svg>';
    handle.addEventListener('mousedown', function () {
      item.draggable = true;
    });
    handle.addEventListener('touchstart', function () {
      item.draggable = true;
    });
    row.appendChild(handle);

    // Visibility checkbox
    var checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'legend-checkbox';
    checkbox.checked = layerDef.visible;
    var self = this;
    checkbox.addEventListener('change', function () {
      layerDef.layer.setVisible(checkbox.checked);
      // First time the user enables the layer, kick off the SLD load
      // (a no-op if the descriptor has no styleUrl, or already loaded).
      if (checkbox.checked) self._loadStyleOnce(layerDef);
      // Update the cartographic key (no-op if no styled layer involved).
      if (self.mapKey) self.mapKey.setVisible(layerDef, checkbox.checked);
    });
    row.appendChild(checkbox);

    // Thumbnail — use explicit thumbnail if provided, otherwise auto-generate
    // from spatialRepresentationType
    var thumb = document.createElement('div');
    thumb.className = 'legend-thumbnail';
    if (layerDef.thumbnail) {
      thumb.innerHTML = layerDef.thumbnail;
    } else {
      var srt = (layerDef.spatialRepType || '').toLowerCase();
      if (srt === 'grid' || srt === 'tin' || srt === 'stereomodel') {
        // Raster/grid icon: 3x3 grid of squares
        thumb.innerHTML = '<svg viewBox="0 0 20 20" width="20" height="20">' +
          '<rect x="1" y="1" width="5" height="5" fill="#0057a8" opacity="0.7"/>' +
          '<rect x="7.5" y="1" width="5" height="5" fill="#4a88c4" opacity="0.5"/>' +
          '<rect x="14" y="1" width="5" height="5" fill="#0057a8" opacity="0.6"/>' +
          '<rect x="1" y="7.5" width="5" height="5" fill="#4a88c4" opacity="0.5"/>' +
          '<rect x="7.5" y="7.5" width="5" height="5" fill="#0057a8" opacity="0.8"/>' +
          '<rect x="14" y="7.5" width="5" height="5" fill="#4a88c4" opacity="0.5"/>' +
          '<rect x="1" y="14" width="5" height="5" fill="#0057a8" opacity="0.6"/>' +
          '<rect x="7.5" y="14" width="5" height="5" fill="#4a88c4" opacity="0.5"/>' +
          '<rect x="14" y="14" width="5" height="5" fill="#0057a8" opacity="0.7"/>' +
          '</svg>';
      } else if (srt) {
        // Vector icon: polygon with vertices
        thumb.innerHTML = '<svg viewBox="0 0 20 20" width="20" height="20">' +
          '<polygon points="3,16 10,2 17,12 12,18" fill="rgba(0,87,168,0.25)" stroke="#0057a8" stroke-width="1.5"/>' +
          '<circle cx="3" cy="16" r="1.8" fill="#0057a8"/>' +
          '<circle cx="10" cy="2" r="1.8" fill="#0057a8"/>' +
          '<circle cx="17" cy="12" r="1.8" fill="#0057a8"/>' +
          '<circle cx="12" cy="18" r="1.8" fill="#0057a8"/>' +
          '</svg>';
      }
    }
    row.appendChild(thumb);

    // Label
    var label = document.createElement('span');
    label.className = 'legend-label';
    label.textContent = layerDef.label;
    row.appendChild(label);

    // Chevron (accordion trigger)
    var chevron = document.createElement('span');
    chevron.className = 'legend-chevron';
    chevron.textContent = '\u25BE'; // small down triangle
    chevron.addEventListener('click', function (e) {
      e.stopPropagation();
      item.classList.toggle('open');
    });
    row.appendChild(chevron);

    item.appendChild(row);

    // ── Accordion panel ────────────────────────────────────────────
    var accordion = this._buildAccordion(layerDef);
    item.appendChild(accordion);

    return item;
  }

  _buildAccordion(layerDef) {
    var accordion = document.createElement('div');
    accordion.className = 'legend-accordion';

    // Opacity slider row
    var opacityRow = document.createElement('div');
    opacityRow.className = 'transparency-row';

    var opacityLabel = document.createElement('label');
    opacityLabel.textContent = 'Opacity';
    opacityRow.appendChild(opacityLabel);

    var slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'legend-opacity-slider';
    slider.min = '1';
    slider.max = '100';
    slider.value = '100';
    opacityRow.appendChild(slider);

    var valueDisplay = document.createElement('span');
    valueDisplay.className = 'legend-opacity-value';
    valueDisplay.textContent = '100%';
    opacityRow.appendChild(valueDisplay);

    // Expose so _loadStyleOnce can re-seed the slider when the SLD
    // hoists a non-default opacity (e.g. the SLD's first rule has
    // fill-opacity=0.6 → slider lands at 60% on first show).
    layerDef._opacitySlider = slider;
    layerDef._opacityDisplay = valueDisplay;

    slider.addEventListener('input', function () {
      var val = parseInt(slider.value, 10);
      valueDisplay.textContent = val + '%';
      layerDef.layer.setOpacity(val / 100);
    });

    accordion.appendChild(opacityRow);

    // Zoom-to-Extent link — built first so it can be paired with a
    // download link in the same row (right-justified across from it).
    // When no download link is renderable but bbox is, the link still
    // appears on its own row, right-justified via the row's flex
    // layout. bbox arrives in CRS84 ([west, south, east, north]) and
    // gets reprojected to the map's EPSG:3857 view extent at click time.
    var selfZoom = this;
    var zoomLink = null;
    if (layerDef.bbox && layerDef.bbox.length === 4) {
      zoomLink = document.createElement('a');
      zoomLink.className = 'legend-zoom-link';
      zoomLink.href = '#';
      zoomLink.textContent = 'Zoom to Extent';
      zoomLink.title = 'Fit the map view to this layer’s bounding box';
      zoomLink.addEventListener('click', function (e) {
        e.preventDefault();
        var extent3857 = transformExtent(layerDef.bbox, 'EPSG:4326', 'EPSG:3857');
        selfZoom.map.getView().fit(extent3857, {
          padding: [40, 40, 40, 40],
          duration: 300,
          maxZoom: 18
        });
      });
    }

    // Download link (conditional)
    if (layerDef.downloadUrl) {
      var dlLink = document.createElement('a');
      dlLink.className = 'legend-download-link';
      dlLink.href = '#';
      dlLink.textContent = 'Download GeoJSON';
      // Inner transfer logic. Wrapped so the Use / Access Conditions
      // gate can call it after the user accepts. The legend item does
      // not carry the conditions text itself, so we fetch them from
      // the per-record metadataUrl (cheap, one request, only fired on
      // a download click).
      var runFeatureDownload = function () {
        dlLink.textContent = 'Downloading...';
        dlLink.style.pointerEvents = 'none';
        var url = layerDef.downloadUrl;
        var sep = url.includes('?') ? '&' : '?';
        fetch(url + sep + 'f=json&limit=10000',{ cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/geo+json' });
            var a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = (layerDef.label || 'data').replace(/[^a-z0-9_-]/gi, '_') + '.geojson';
            a.click();
            URL.revokeObjectURL(a.href);
            dlLink.textContent = 'Download GeoJSON';
            dlLink.style.pointerEvents = '';
          })
          .catch(function (err) {
            dlLink.textContent = 'Download failed';
            setTimeout(function () {
              dlLink.textContent = 'Download GeoJSON';
              dlLink.style.pointerEvents = '';
            }, 2000);
          });
      };
      dlLink.addEventListener('click', function (e) {
        e.preventDefault();
        if (layerDef.metadataUrl) {
          SecimsConditionsConfirm.showForRecordUrl(layerDef.metadataUrl,
            runFeatureDownload, { title: layerDef.label || '' });
        } else {
          SecimsConditionsConfirm.show({}, runFeatureDownload,
            { title: layerDef.label || '' });
        }
      });
      var dlRow = document.createElement('div');
      dlRow.className = 'legend-link-row';
      dlRow.appendChild(dlLink);
      if (zoomLink) { dlRow.appendChild(zoomLink); zoomLink = null; }
      accordion.appendChild(dlRow);
    }

    // Metadata link (conditional) — opens modal instead of navigating.
    // For web-service layers (no download or coverage link), pair the
    // Metadata link with Zoom to Extent so the zoom action sits across
    // from a populated link rather than floating on its own row.
    if (layerDef.metadataUrl) {
      var self = this;
      var mdLink = document.createElement('a');
      mdLink.className = 'legend-metadata-link';
      mdLink.href = '#';
      mdLink.textContent = 'Metadata';
      mdLink.addEventListener('click', function (e) {
        e.preventDefault();
        self._showMetadataModal(layerDef.metadataUrl, layerDef.label);
      });
      // Pair Metadata with Zoom to Extent whenever there's no
      // "Download GeoJSON" row to consume the zoom link. Covers both
      // raster (coverageUrl set, but its long "(current map extent)"
      // note crowds the row) and web-service layers (no download link
      // at all) — in both cases the metadata link is the better
      // pairing target.
      if (zoomLink && !layerDef.downloadUrl) {
        var mdRow = document.createElement('div');
        mdRow.className = 'legend-link-row';
        mdRow.appendChild(mdLink);
        mdRow.appendChild(zoomLink);
        zoomLink = null;
        accordion.appendChild(mdRow);
      } else {
        accordion.appendChild(mdLink);
      }
    }

    // Coverage download (raster, clipped to current map extent).
    // Reads the parent map's view at click time so the user can pan/zoom
    // to set the extent before clicking. Builds an OGC API - Coverages
    // bbox-subset URL: GET /collections/{id}/coverage?bbox=W,S,E,N&f=GTiff
    // (matches the format used by _createCoverageLayer for consistency).
    if (layerDef.coverageUrl) {
      var selfCov = this;
      var covLink = document.createElement('a');
      covLink.className = 'legend-download-link';
      covLink.href = '#';
      covLink.title = 'Download a GeoTIFF clipped to the current map extent';
      covLink.appendChild(document.createTextNode('Download GeoTIFF '));
      var covNote = document.createElement('span');
      covNote.className = 'legend-download-note';
      covNote.textContent = '(current map extent)';
      covLink.appendChild(covNote);
      covLink.addEventListener('click', function (e) {
        e.preventDefault();
        var runCoverageDownload = function () {
          var view = selfCov.map.getView();
          var extent3857 = view.calculateExtent(selfCov.map.getSize());
          var extent4326 = transformExtent(extent3857, 'EPSG:3857', 'EPSG:4326');
          var bbox = extent4326.map(function (n) { return n.toFixed(6); }).join(',');
          var base = layerDef.coverageUrl;
          var sep = base.indexOf('?') >= 0 ? '&' : '?';
          var url = base + sep + 'bbox=' + encodeURIComponent(bbox) + '&f=GTiff';
          window.open(url, '_blank');
        };
        if (layerDef.metadataUrl) {
          SecimsConditionsConfirm.showForRecordUrl(layerDef.metadataUrl,
            runCoverageDownload, { title: layerDef.label || '' });
        } else {
          SecimsConditionsConfirm.show({}, runCoverageDownload,
            { title: layerDef.label || '' });
        }
      });
      // zoomLink is normally consumed earlier by the Metadata branch
      // (the cleaner pairing on raster layers), so coverage links
      // appear unwrapped. If there's no metadataUrl on this record
      // we still pair them here so zoom never disappears.
      if (zoomLink) {
        var covRow = document.createElement('div');
        covRow.className = 'legend-link-row';
        covRow.appendChild(covLink);
        covRow.appendChild(zoomLink);
        zoomLink = null;
        accordion.appendChild(covRow);
      } else {
        accordion.appendChild(covLink);
      }
    }

    // No download link consumed the zoom link — render it on its own
    // row so it still floats right (via .legend-link-row's flex rule).
    if (zoomLink) {
      var zoomRow = document.createElement('div');
      zoomRow.className = 'legend-link-row';
      zoomRow.appendChild(zoomLink);
      accordion.appendChild(zoomRow);
    }

    return accordion;
  }

  // ── Deep-link entry point ───────────────────────────────────────────

  /**
   * Toggle the layer whose underlying OL layer carries the given
   * catalogId (set by _createFeatureLayer / _createTileLayer /
   * _createCoverageLayer and the web-service factories). Used by
   * `/map/?layer=<record_id>` deep-links from the landing-page Featured
   * datasets and search results: the matching legend checkbox is
   * checked, the panel is expanded if it was collapsed, the per-layer
   * accordion is opened, and the map view is fitted to the layer's
   * bounding box — so the deep-link arrives at the same view the user
   * would land on after manually ticking and clicking "Zoom to Extent".
   * The Metadata modal is only opened from the explicit "Metadata"
   * link inside each legend row, so we do not auto-open it here.
   *
   * Falls back to opening the metadata modal alone when no legend item
   * matches — typically because the record exists in pycsw but has not
   * been published to pygeoapi yet. Opening just the modal is more
   * helpful than silently doing nothing.
   */
  activateLayerById(recordId) {
    if (!recordId) return;
    var items = this.panelEl.querySelectorAll('.legend-item');
    var match = null;
    for (var i = 0; i < items.length; i++) {
      var def = items[i]._layerDef;
      if (def && def.layer && def.layer.get('catalogId') === recordId) {
        match = items[i];
        break;
      }
    }
    if (match) {
      // Make sure the panel is open so the toggled layer is visible
      // to the user (the legend defaults to expanded, but a previous
      // visit may have collapsed it).
      this.panelEl.classList.remove('collapsed');
      var checkbox = match.querySelector('.legend-checkbox');
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change'));
      }
      // Expand the per-layer accordion (opacity slider, download links,
      // metadata link, etc.) so the deep-linked layer arrives in its
      // fully-revealed state rather than collapsed behind the chevron.
      match.classList.add('open');
      match.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      // Zoom the map to the layer's extent. Same transform + fit
      // options used by the per-layer "Zoom to Extent" accordion link
      // (see _buildAccordion zoomLink). Skipped when the layer carries
      // no bbox — some web-service layers don't expose one.
      var layerDef = match._layerDef;
      if (layerDef && Array.isArray(layerDef.bbox) && layerDef.bbox.length === 4) {
        var extent3857 = transformExtent(layerDef.bbox, 'EPSG:4326', 'EPSG:3857');
        this.map.getView().fit(extent3857, {
          padding: [40, 40, 40, 40],
          duration: 300,
          maxZoom: 18
        });
      }
      return;
    }
    // Fallback: record not yet in the legend. Open the modal so the
    // deep-link still surfaces information rather than silently no-oping.
    var fallbackUrl = '/collections/metadata:main/items/' +
                      encodeURIComponent(recordId) + '?f=json';
    this._showMetadataModal(fallbackUrl, recordId);
  }

  // ── Metadata modal ──────────────────────────────────────────────────

  _showMetadataModal(url, title) {
    // Remove existing modal if any
    var existing = document.getElementById('metadata-modal');
    if (existing) existing.remove();

    // Build modal overlay
    var overlay = document.createElement('div');
    overlay.id = 'metadata-modal';
    var safeTitle = this._escHtml(title || 'Metadata');
    overlay.innerHTML =
      '<div class="metadata-modal-backdrop"></div>' +
      '<div class="metadata-modal-content">' +
        '<div class="metadata-modal-header">' +
          '<h3>' + safeTitle + '</h3>' +
          '<div class="metadata-modal-actions">' +
            '<button class="metadata-modal-pdf" title="Download as PDF">&#128438; PDF</button>' +
            '<button class="metadata-modal-close">&times;</button>' +
          '</div>' +
        '</div>' +
        '<div class="metadata-modal-body">' +
          '<div class="metadata-modal-loading">Loading metadata...</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    // Close handlers
    var bboxMap = null;
    var close = function () {
      if (bboxMap) { bboxMap.setTarget(null); bboxMap = null; }
      overlay.remove();
    };
    overlay.querySelector('.metadata-modal-backdrop').addEventListener('click', close);
    overlay.querySelector('.metadata-modal-close').addEventListener('click', close);
    document.addEventListener('keydown', function handler(e) {
      if (e.key === 'Escape') { close(); document.removeEventListener('keydown', handler); }
    });

    // PDF handler
    overlay.querySelector('.metadata-modal-pdf').addEventListener('click', function () {
      var body = overlay.querySelector('.metadata-modal-body');
      // Capture the live map, then swap the placeholder div in a CLONE of
      // the body for an <img> of the rendered snapshot. Going via the DOM
      // (rather than regex on innerHTML) is necessary because OL has
      // filled the placeholder with canvas children — the placeholder
      // is no longer an empty <div></div> by the time we serialize.
      SecimsBboxThumbnail.captureBboxMap(bboxMap).then(function (dataUrl) {
        var clone = body.cloneNode(true);
        if (dataUrl) {
          var placeholder = clone.querySelector('.metadata-bbox-map');
          if (placeholder) {
            var img = document.createElement('img');
            img.className = 'metadata-bbox-map-image';
            img.src = dataUrl;
            img.alt = 'Bounding box map';
            placeholder.replaceWith(img);
          }
        }
        _printMetadataAsPdf(safeTitle, clone.innerHTML);
      });
    });

    // Fetch and render
    var body = overlay.querySelector('.metadata-modal-body');
    var self = this;
    fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        body.innerHTML = _renderMetadata(data);
        bboxMap = SecimsBboxThumbnail.initBboxMap(body, data, { olDeps: OL_DEPS });

        // Prepend a primary download button to the header when the record
        // exposes a downloadable resource (vector → GeoJSON via OGC API
        // Features, raster/document → original file via /api/download).
        // Mirrors the catalog modal so both entry points behave the same.
        var dlInfo = self._getMetadataDownloadInfo(data);
        if (dlInfo) {
          var actions = overlay.querySelector('.metadata-modal-actions');
          var dlBtn = document.createElement('button');
          dlBtn.className = 'metadata-modal-download';
          dlBtn.textContent = dlInfo.kind === 'features' ? 'Download GeoJSON' : 'Download';
          dlBtn.title = dlInfo.kind === 'features'
            ? 'Download the full feature collection as GeoJSON'
            : 'Download the source file';
          dlBtn.addEventListener('click', function () {
            var props = (data && data.properties) || {};
            SecimsConditionsConfirm.show(props, function () {
              self._runMetadataDownload(dlInfo, dlBtn);
            }, { title: props.title || (data && data.id) || '' });
          });
          actions.insertBefore(dlBtn, actions.firstChild);
        }
      })
      .catch(function (err) {
        body.innerHTML = '<div class="metadata-modal-error">Failed to load metadata: ' + err.message + '</div>';
      });
  }

  // Derive a download endpoint from a freshly fetched OGC API record.
  // Vector layers get a GeoJSON export via OGC API - Features; raster
  // layers and document records fall back to the original file via the
  // SEC-IMS /api/download passthrough. Returns null when nothing is
  // downloadable (e.g. service-only records or records that pre-date
  // the upload pipeline).
  _getMetadataDownloadInfo(data) {
    var links = (data && data.links) || [];
    var props = (data && data.properties) || {};
    var displayName = props.title || data.id || 'dataset';
    var featuresLink = links.find(function (l) { return l.protocol === 'OGC:API - Features'; });
    if (featuresLink && featuresLink.href) {
      return { kind: 'features', url: featuresLink.href, name: displayName };
    }
    var fileLink = links.find(function (l) { return l.protocol === 'urn:x-secims:file:id'; });
    if (fileLink && fileLink.href) {
      var fileId = fileLink.href.replace('urn:x-secims:file:', '');
      if (fileId) {
        // Map viewer is served from the same origin as pycsw — relative
        // path keeps the modal independent of any frontend host config.
        return { kind: 'file', url: '/api/download/' + fileId, name: displayName };
      }
    }
    return null;
  }

  _runMetadataDownload(info, btn) {
    if (!info) return;
    if (info.kind === 'file') {
      var a = document.createElement('a');
      a.href = info.url;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Downloading…';
    var sep = info.url.indexOf('?') >= 0 ? '&' : '?';
    fetch(info.url + sep + 'f=json&limit=10000',{ cache: 'no-store' } )
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/geo+json' });
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = (info.name || 'data').replace(/[^a-z0-9_-]/gi, '_') + '.geojson';
        a.click();
        URL.revokeObjectURL(a.href);
        btn.textContent = originalLabel;
        btn.disabled = false;
      })
      .catch(function () {
        btn.textContent = 'Download failed';
        setTimeout(function () {
          btn.textContent = originalLabel;
          btn.disabled = false;
        }, 2000);
      });
  }

  _escHtml(str) {
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  // ── Collapse / expand toggle ───────────────────────────────────────

  _bindCollapseToggle() {
    var self = this;
    this.toggleEl.addEventListener('click', function () {
      self.panelEl.classList.toggle('collapsed');
    });
  }

  // ── Drag-and-drop reorder (within section) ─────────────────────────

  _bindDragEvents(container, sectionKey) {
    var self = this;

    container.addEventListener('dragstart', function (e) {
      var item = e.target.closest('.legend-item');
      if (!item) return;
      self._dragSourceItem = item;
      self._dragSourceSection = sectionKey;
      item.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
      // Required for Firefox
      e.dataTransfer.setData('text/plain', '');
    });

    container.addEventListener('dragover', function (e) {
      e.preventDefault();
      // Block cross-section drops
      if (self._dragSourceSection !== sectionKey) {
        e.dataTransfer.dropEffect = 'none';
        return;
      }
      e.dataTransfer.dropEffect = 'move';

      var target = e.target.closest('.legend-item');
      if (!target || target === self._dragSourceItem) return;

      // Clear previous indicators
      self._clearDragIndicators(container);
      target.classList.add('drag-over');
    });

    container.addEventListener('dragleave', function (e) {
      var target = e.target.closest('.legend-item');
      if (target) {
        target.classList.remove('drag-over');
      }
    });

    container.addEventListener('drop', function (e) {
      e.preventDefault();
      self._clearDragIndicators(container);

      if (self._dragSourceSection !== sectionKey) return;

      var target = e.target.closest('.legend-item');
      if (!target || target === self._dragSourceItem) return;

      // Determine insertion position
      var items = Array.prototype.slice.call(container.querySelectorAll('.legend-item'));
      var dragIndex = items.indexOf(self._dragSourceItem);
      var dropIndex = items.indexOf(target);

      if (dragIndex < dropIndex) {
        container.insertBefore(self._dragSourceItem, target.nextSibling);
      } else {
        container.insertBefore(self._dragSourceItem, target);
      }

      // Sync map layer order after DOM reorder
      self._syncMapLayerOrder();
    });

    container.addEventListener('dragend', function () {
      if (self._dragSourceItem) {
        self._dragSourceItem.classList.remove('dragging');
        self._dragSourceItem.draggable = false;
      }
      self._clearDragIndicators(container);
      self._dragSourceItem = null;
      self._dragSourceSection = null;
    });
  }

  _clearDragIndicators(container) {
    var items = container.querySelectorAll('.legend-item.drag-over');
    for (var i = 0; i < items.length; i++) {
      items[i].classList.remove('drag-over');
    }
  }

  // ── Map layer order synchronisation ────────────────────────────────
  //
  // OL layer stack: index 0 = bottom-most, highest index = top-most
  // Legend: first item in each section = topmost on map
  //
  // Stack (bottom → top): basemap → web services → project layers.
  // Web services sit above basemaps so users can overlay external WMS
  // imagery on top of the basemap, and below project data so internal
  // datasets remain the focus of the viewer.
  // Within each section, the first legend item is the topmost of that group.

  _syncMapLayerOrder() {
    var layers = this.map.getLayers();

    // Collect layers from the legend DOM in rendering order (bottom to top)
    var orderedLayers = [];

    // Basemap layers (reversed: last legend item = bottom of stack)
    var basemapItems = this.basemapSectionEl.querySelectorAll('.legend-item');
    for (var i = basemapItems.length - 1; i >= 0; i--) {
      orderedLayers.push(basemapItems[i]._layerDef.layer);
    }

    // Web services (reversed: last legend item = bottom of group)
    var webServiceItems = this.webServiceSectionEl.querySelectorAll('.legend-item');
    for (var w = webServiceItems.length - 1; w >= 0; w--) {
      orderedLayers.push(webServiceItems[w]._layerDef.layer);
    }

    // Project layers (reversed: last legend item = bottom of project stack)
    var projectItems = this.projectSectionEl.querySelectorAll('.legend-item');
    for (var j = projectItems.length - 1; j >= 0; j--) {
      orderedLayers.push(projectItems[j]._layerDef.layer);
    }

    // Rebuild the OL layer collection preserving any layers not in the legend
    // (e.g. the measurement tool's vector layer)
    var currentLayers = layers.getArray().slice();
    var legendLayerSet = [];
    for (var k = 0; k < orderedLayers.length; k++) {
      legendLayerSet.push(orderedLayers[k]);
    }

    // Find non-legend layers (measurement layer, etc.)
    var nonLegendLayers = [];
    for (var m = 0; m < currentLayers.length; m++) {
      if (legendLayerSet.indexOf(currentLayers[m]) === -1) {
        nonLegendLayers.push(currentLayers[m]);
      }
    }

    // Clear and rebuild: legend layers first (bottom), then non-legend on top
    layers.clear();
    for (var n = 0; n < orderedLayers.length; n++) {
      layers.push(orderedLayers[n]);
    }
    for (var p = 0; p < nonLegendLayers.length; p++) {
      layers.push(nonLegendLayers[p]);
    }
  }
}
